<template>
  <div>
    <template>
      <div>
        <strong> {{ scanBodySelectionLabel?.toUpperCase() }} </strong>
      </div>
      <span class="primary--text">
        Select the <strong> top facet </strong> of each of the
        <strong> {{ scanBodySelectionLabel }} </strong> in each scan to prepare.
      </span>
    </template>
    <v-card flat class="d-flex">
      <template>
        <v-card-text>
          <strong>Computer</strong>
          <ul>
            <li>Left-click to select.</li>
            <li>Click any facet again to unselect.</li>
            <li>Rotate scan by holding left-click and dragging.</li>
            <li>Use mouse scroll-wheel to zoom.</li>
          </ul>
        </v-card-text>
        <v-card-text>
          <strong>Touch Devices</strong>
          <ul>
            <li>Tap to select.</li>
            <li>Tap again to unselect.</li>
            <li>Hold and drag to rotate scan.</li>
            <li>Pinch to zoom.</li>
          </ul>
        </v-card-text>
      </template>
    </v-card>
    <div class="d-flex">
      <v-row>
        <v-col>
          <template v-if="!selectGaugesResponse">
            <v-btn
              class="elevation-0 white--text text-body-2 font-weight-medium btn-primary mr-3"
              @click="select"
              :disabled="loading"
            >
              Confirm {{ scanBodySelectionLabel }}
            </v-btn>
            <v-btn
              class="elevation-0 rounded-10 text-body-2 font-weight-medium"
              @click="clearSelections"
              :disabled="loading"
            >
              Clear All Selections
            </v-btn>
          </template>
        </v-col>
        <v-col cols="12" class="pt-0 ps-0">
          <v-scale-transition origin="center center">
            <div class="flex-grow-1 ml-3 mr-1" v-if="loading">
              <v-progress-linear
                rounded
                indeterminate
                color="primary"
                height="36"
                class="rounded-10"
                background-opacity="0.6"
              >
                <span
                  class="text-body-2 font-weight-medium white--text loading-pulse"
                >
                  PROCESSING...
                </span>
              </v-progress-linear>
            </div>
          </v-scale-transition>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import { getScanAnalysis } from "@/utils"

export default {
  name: "PreAlignment",

  scan1View: null,
  scan2View: null,

  data() {
    return {
      loading: false,
      selectGaugesResponse: null
    }
  },

  computed: {
    ...mapGetters([
      "ncSessionID",
      "retrying",
      "addScansResponse",
      "faceCount",
      "identity",
      "sessionType",
      "narrowScanBodiesPresent",
      "scanBodySelectionLabel",
      "validateScansResponse",
      "validationPass",
      "validationReconsider",
      "analyzerSession",
      "kitBoxID"
    ])
  },

  methods: {
    ...mapActions([
      "setValidateScansResponse",
      "setScanBodySelectionLabel",
      "setPageState",
      "setTransition",
      "setRetrying",
      "setFaceCount",
      "setErrorMessage",
      "setAnalyzerSession"
    ]),

    async select() {
      this.scan1View = NCFrontend.get("scan1View")
      this.scan2View = NCFrontend.get("scan2View")

      const scan1Visualisation = this.scan1View.visualisation
      const scan2Visualisation = this.scan2View.visualisation

      const scan1SelectedPlaneIndicesForScanGauges =
        scan1Visualisation.selectedPlaneIndicesForScanGauges
      const scan2SelectedPlaneIndicesForScanGauges =
        scan2Visualisation.selectedPlaneIndicesForScanGauges

      if (
        scan1SelectedPlaneIndicesForScanGauges.length === 0 ||
        scan2SelectedPlaneIndicesForScanGauges.length === 0
      ) {
        this.setErrorMessage("No Scan Gauges have been selected.")
        return
      }
      if (
        scan1SelectedPlaneIndicesForScanGauges.length !==
        scan2SelectedPlaneIndicesForScanGauges.length
      ) {
        this.setErrorMessage("Mismatching number of Scan Gauges selected.")
        return
      }

      let scan1SelectedPlaneIndicesForNarrowScanBodies = []
      let scan2SelectedPlaneIndicesForNarrowScanBodies = []
      if (this.narrowScanBodiesPresent) {
        if (
          scan1Visualisation.selectingNarrowScanBodies &&
          scan2Visualisation.selectingNarrowScanBodies
        ) {
          scan1SelectedPlaneIndicesForNarrowScanBodies =
            scan1Visualisation.selectedPlaneIndicesForNarrowScanBodies
          scan2SelectedPlaneIndicesForNarrowScanBodies =
            scan2Visualisation.selectedPlaneIndicesForNarrowScanBodies
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length === 0 ||
            scan2SelectedPlaneIndicesForNarrowScanBodies.length === 0
          ) {
            this.setErrorMessage("No Narrow Scan Bodies have been selected.")
            return
          }
          if (
            scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
            scan2SelectedPlaneIndicesForNarrowScanBodies.length
          ) {
            this.setErrorMessage(
              "Mismatching number of Narrow Scan Bodies selected."
            )
            return
          }
        } else {
          scan1Visualisation.startSelectionForNarrowScanBodies()
          scan2Visualisation.startSelectionForNarrowScanBodies()
          this.setScanBodySelectionLabel("Narrow Scan Bodies")
          return
        }
      }

      if (
        this.faceCount !== null &&
        scan1SelectedPlaneIndicesForScanGauges.length +
          scan1SelectedPlaneIndicesForNarrowScanBodies.length !==
          this.faceCount
      ) {
        this.setErrorMessage(
          `Selection Mismatch: Must select ${this.faceCount} Scan Gauges`
        )
        return
      }

      try {
        this.loading = true
        scan1Visualisation.stopSelection()
        scan2Visualisation.stopSelection()
        const selectGaugesResponse =
          await NCFrontend.ApiClient.sessionSelectGauges("/proxy", "", {
            sessionId: this.ncSessionID,
            scan1Id: this.addScansResponse.scan1.id,
            scan2Id: this.addScansResponse.scan2.id,
            scan1SelectedPlaneIndicesForScanGauges,
            scan1SelectedPlaneIndicesForNarrowScanBodies,
            scan2SelectedPlaneIndicesForScanGauges,
            scan2SelectedPlaneIndicesForNarrowScanBodies
          })

        await Promise.all([
          scan1Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan1.scanGauges,
            selectGaugesResponse.scan1.narrowScanBodies
          ),
          scan2Visualisation.showSelectedScanBodies(
            selectGaugesResponse.scan2.scanGauges,
            selectGaugesResponse.scan2.narrowScanBodies
          )
        ])
        this.selectGaugesResponse = selectGaugesResponse

        await this.align()
        this.setScanBodySelectionLabel(null)
      } catch (err) {
        this.setErrorMessage("An internal error occurred.")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    async align() {
      this.setValidateScansResponse(
        await NCFrontend.ApiClient.sessionValidateScans("/proxy", "", {
          sessionId: this.ncSessionID,
          scan1Id: this.addScansResponse.scan1.id,
          scan2Id: this.addScansResponse.scan2.id,
          retrying: this.retrying
        })
      )

      if (!this.retrying) {
        const response = await this.$axios.post("/proxy/analyzer_session/", {
          actor_uid: this.identity.uid,
          actor_payload: this.identity,
          kit_serial_number: this.kitBoxID?.kit_box_id,
          session_type: this.sessionType,
          nc_session_id: this.ncSessionID,
          scan_analysis: getScanAnalysis(this.validateScansResponse)
        })
        this.setAnalyzerSession(response.data)
      } else if (this.analyzerSession) {
        await this.$axios.put(
          `/proxy/analyzer_session/${this.analyzerSession.uid}/`,
          {
            actor_uid: this.identity.uid,
            actor_payload: this.identity,
            kit_serial_number: this.kitBoxID?.kit_box_id,
            session_type: this.sessionType,
            nc_session_id: this.ncSessionID,
            scan_analysis: getScanAnalysis(this.validateScansResponse)
          }
        )
      }

      this.setTransition(true)
      if (!this.validationPass || this.validationReconsider)
        this.setPageState([
          { cols: 3, component: "SuggestedSolutions" },
          { cols: 5, component: "ValidatedDiv" }
        ])
      else this.setPageState([{ cols: 7, component: "ValidatedDiv" }])

      if (this.faceCount === null) {
        const visualisation1 = NCFrontend.get("scan1View").visualisation
        const gaugeCount =
          visualisation1.selectedPlaneIndicesForScanGauges?.length || 0
        const narrowCount =
          visualisation1.scan1SelectedPlaneIndicesForNarrowScanBodies?.length ||
          0

        this.setFaceCount(gaugeCount + narrowCount)
      }

      this.setRetrying(false)

      this.$emit("switch")
    },

    clearSelections() {
      NCFrontend.get("scan1View").visualisation.clearSelections()
      NCFrontend.get("scan2View").visualisation.clearSelections()

      NCFrontend.get("scan1View").visualisation.startSelectionForScanGauges()
      NCFrontend.get("scan2View").visualisation.startSelectionForScanGauges()

      NCFrontend.get("scan1View").visualisation.clearSelections()
      NCFrontend.get("scan2View").visualisation.clearSelections()

      this.setScanBodySelectionLabel("Scan Gauges")
    }
  }
}
</script>
